import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { LockIcon, PlusIcon, Share2Icon } from 'lucide-react';
import { ShareSheet } from '../../components/engagement/ShareSheet';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/ui/Button';
import { Card, SectionTitle } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { progressPhotos } from '../../data/engagement';
import { useLocale } from '../../contexts/LocaleContext';

/** MOB/Profile/ProgressPhotos — private-by-default evolution timeline. */
export function ProgressPhotos() {
  const {
    t,
    tl,
    locale
  } = useLocale();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [addNote, setAddNote] = useState(false);
  const dateLocale = locale === 'pt-BR' ? ptBR : undefined;
  const fmt = (iso: string) => format(parseISO(iso), 'dd MMM yyyy', {
    locale: dateLocale
  });
  const latest = progressPhotos[0];
  const first = progressPhotos[progressPhotos.length - 1];
  return <div className="px-2 pb-2" data-id="element-2529" data-mp="pwn5m4dhkfn3">
      <PageHeader title={t('eng.photos.title')} subtitle={t('eng.photos.subtitle')} showBack action={<span className="bee-overline flex items-center gap-1 rounded-pill border border-sand-line px-2 py-[5px] text-[8.5px] font-bold text-deep" data-id="element-2531" data-mp="h1z1wink760e">
            <LockIcon size={10} aria-hidden="true" data-id="element-2532" data-mp="14q8gazig0rhi" />
            {t('eng.photos.private')}
          </span>} data-id="element-2530" data-mp="pwwyq0ttv2x0" />

      <Card className="p-2" data-id="element-2533" data-mp="uu5e5tnlcsuk">
        <p className="text-[12px] leading-snug text-muted" data-id="element-2534" data-mp="uuyi6mv61490">{t('eng.photos.privateNote')}</p>
      </Card>

      <section className="mt-3" data-id="element-2535" data-mp="zs70qok87aas">
        <SectionTitle data-id="element-2536" data-mp="zvcpg2oy0a5z">{t('eng.photos.compare')}</SectionTitle>
        <div className="grid grid-cols-2 gap-2" data-id="element-2537" data-mp="3fjcpcozayy5b">
          {[{
          photo: first,
          label: t('eng.photos.first')
        }, {
          photo: latest,
          label: t('eng.photos.latest')
        }].map(({
          photo,
          label
        }) => <figure key={photo.id} className="overflow-hidden rounded-card bg-sand-deep" data-id="element-2538" data-mp="30gl1lqf39glx">
              <img src={photo.url} alt={t('eng.photos.alt', {
            date: fmt(photo.date)
          })} className="aspect-[3/4] w-full object-cover" data-id="element-2539" data-mp="2wgaxkjaux09p" />
              <figcaption className="px-2 py-1" data-id="element-2540" data-mp="2mpg9rahypsm5">
                <span className="bee-overline block text-[8.5px] font-bold text-muted" data-id="element-2541" data-mp="2hqmv3eqxvvke">{label}</span>
                <span className="bee-num text-[12px] font-medium text-deep" data-id="element-2542" data-mp="2cs1rd5chatr7">{fmt(photo.date)}</span>
              </figcaption>
            </figure>)}
        </div>
      </section>

      <section className="mt-3" data-id="element-2543" data-mp="23r3ncn21im8m">
        <SectionTitle action={<Button variant="quiet" onClick={() => setAddNote(true)} data-id="element-2545" data-mp="23t6nhzwwpvr2">
              <PlusIcon size={15} aria-hidden="true" data-id="element-2546" data-mp="6xinzutn3yz8" />
              {t('eng.photos.add')}
            </Button>} data-id="element-2544" data-mp="23rvla4j40w22">
          {t('eng.photos.timeline')}
        </SectionTitle>
        {addNote && <p className="mb-2 rounded-card border border-sand-line bg-sand-deep/60 p-2 text-[11.5px] text-deep" data-id="element-2547" data-mp="9iqb6u9fbi8l">
            {t('eng.photos.addedNote')}
          </p>}
        <ol className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-2548" data-mp="ja49iypn2c05">
          {progressPhotos.map(photo => <li key={photo.id} className="flex items-center gap-2 border-b border-sand-line py-2 last:border-b-0" data-id="element-2549" data-mp="o7cs30ep8i1x">
              <img src={photo.url} alt={t('eng.photos.alt', {
            date: fmt(photo.date)
          })} className="h-16 w-12 shrink-0 rounded-[10px] object-cover" data-id="element-2550" data-mp="3rocw9q5hc8ke" />
              <span className="min-w-0 flex-1" data-id="element-2551" data-mp="4l81v2rhv102">
                <span className="bee-num block text-[13px] font-medium text-deep" data-id="element-2552" data-mp="4htz3tex4ps3">{fmt(photo.date)}</span>
                <span className="block text-[11.5px] text-muted" data-id="element-2553" data-mp="1c034tbuff5d9">{tl(photo.note)}</span>
              </span>
              <button type="button" onClick={() => setConfirmId(photo.id)} aria-label={`${t('eng.photos.share')} · ${fmt(photo.date)}`} className="grid h-11 w-11 shrink-0 place-items-center rounded-pill text-muted transition-colors duration-150 ease-brand hover:bg-sand-deep hover:text-deep" data-id="element-2554" data-mp="1ei6j50lsv4pm">
                <Share2Icon size={16} data-id="element-2555" data-mp="1jjcbuyfgmrz5" />
              </button>
            </li>)}
        </ol>
      </section>

      <Modal open={confirmId !== null} onClose={() => setConfirmId(null)} labelledBy="photo-share-title" data-id="element-2556" data-mp="1shxq4hdwfku4">
        <h2 id="photo-share-title" className="pr-5 text-section text-deep" data-id="element-2557" data-mp="276u4sxagnw3p">
          {t('eng.photos.shareTitle')}
        </h2>
        <p className="mt-1 text-[13px] leading-relaxed text-muted" data-id="element-2558" data-mp="227ha5qdclkqk">{t('eng.photos.shareBody')}</p>
        <div className="mt-3 space-y-2" data-id="element-2559" data-mp="2g6qsavl46ox6">
          <Button variant="primary" size="lg" fullWidth onClick={() => {
          setConfirmId(null);
          setSharing(true);
        }} data-id="element-2560" data-mp="2g7s5b5loxlhj">
            {t('eng.photos.shareConfirm')}
          </Button>
          <Button variant="outline" size="lg" fullWidth onClick={() => setConfirmId(null)} data-id="element-2561" data-mp="2zs0qgseyzdpw">
            {t('eng.photos.shareCancel')}
          </Button>
        </div>
      </Modal>

      <ShareSheet open={sharing} onClose={() => setSharing(false)} data-id="element-2562" data-mp="2qrue59v0cjsb" />
    </div>;
}